import { useDispatch } from 'react-redux';
import {
  addCredentialFromForm,
  addDataSourceFromForm,
  updateDataSourceFromForm,
  updateSinkInfoFromForm,
  updateSinkTypeFromForm,
  verifyCredentialFromForm,
  verifyCredentialSilent,
} from 'js/actions';
import { useCallback } from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

/**
 * useFormsAdapter creates utils functions based on the FormType and
 * returns them. e.g. createSource, createSink, etc.
 */
export default function useFormsAdapter(formType: string) {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

  const createSource = useCallback(
    ({
      sourceType,
      connectionCategory = null,
      sourceInfo,
      credentialId,
      ignoreCredentials = false,
      shouldActivateSource = true,
    }) => {
      return dispatch(
        addDataSourceFromForm(
          formType,
          sourceType,
          connectionCategory,
          sourceInfo,
          credentialId,
          ignoreCredentials,
          shouldActivateSource,
        ),
      );
    },
    [formType],
  );

  const createSink = useCallback(
    ({ formType, sinkType, sinkInfo, credentialId }) => {
      return dispatch(updateSinkTypeFromForm(formType, sinkType, sinkInfo, credentialId));
    },
    [formType],
  );

  const updateSource = useCallback(
    ({
      connectionCategory = null,
      sourceInfo,
      credentialId,
      shouldActivateSource = false,
      sourceType = null,
      credentialGroupId = null,
      runWithIngestion = false,
    }) => {
      const id = sourceInfo.id;
      // deleting it because backend does not expect this property
      delete sourceInfo.id;
      return dispatch(
        updateDataSourceFromForm(
          formType,
          id,
          sourceInfo,
          connectionCategory,
          shouldActivateSource,
          credentialId,
          sourceType,
          credentialGroupId,
          runWithIngestion,
        ),
      );
    },
    [formType],
  );

  const updateSink = useCallback(
    ({ sinkInfo }) => {
      try {
        const id = sinkInfo?.id;
        // deleting it because backend does not expect this property
        delete sinkInfo.id;
        return dispatch(updateSinkInfoFromForm(formType, id, sinkInfo));
      } catch (e) {
        console.error('useFormsAdapter.updateSink: ', e);
        return null;
      }
    },
    [formType],
  );

  const verifyCredential = useCallback(
    (credentialId: number) => {
      return dispatch(verifyCredentialFromForm(formType, credentialId));
    },
    [formType],
  );

  const _verifyCredentialSilent = useCallback(
    (credId: number) => dispatch(verifyCredentialSilent(credId)),
    [],
  );

  const addCredential = useCallback(
    (credentialType: string, credentialInfo: any) => {
      return dispatch(addCredentialFromForm(formType, credentialType, credentialInfo));
    },
    [formType],
  );

  return {
    createSource,
    createSink,
    updateSource,
    updateSink,
    addCredential,
    verifyCredential,
    verifyCredentialSilent: _verifyCredentialSilent,
  };
}
